'use client';

import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';
import useWebSocketStore, { WS_SERVERS, WebSocketMsg as StoreWebSocketMsg, ServerKey } from '@/lib/hooks/zustand-stores/use-websocket-store';
import { useLogger } from '@/lib/hooks/useLogger';
import { getToken } from '@/app/actions/auth';
import { getEnvironmentDatabaseName } from '@/lib/utils/environment';

// Global connection tracking to prevent multiple connections
let globalSocket: Socket | null = null;
let globalConnectionAttemptInProgress = false;
let globalConnectionCount = 0;

// Define our own ReadyState enum to match standard WebSocket states
export enum ReadyState {
  UNINSTANTIATED = -1,
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3
}

// Define return type for the hook
export interface UseManagedWebSocketReturn {
  /**
   * Send a message through the websocket
   */
  sendMessage: (type: string, data: any, options?: SendMessageOptions) => boolean;
  
  /**
   * Current readyState of the WebSocket
   */
  readyState: ReadyState;
  
  /**
   * Current connection state as a string
   */
  connectionState: string;
  
  /**
   * URL of the WebSocket connection
   */
  socketUrl: string;
  
  /**
   * Current latency in milliseconds
   */
  latency: number | null;
  
  /**
   * Socket.IO instance (if connected)
   */
  socket: Socket | null;
  
  /**
   * Check if a server is available
   */
  checkServerAvailability: () => Promise<boolean>;
}

// Define options for the hook
export interface UseManagedWebSocketOptions {
  /**
   * Whether to automatically connect on mount
   * @default true
   */
  autoConnect?: boolean;
  
  /**
   * The number of reconnect attempts before giving up
   * @default 10
   */
  reconnectAttempts?: number;
  
  /**
   * The interval between reconnect attempts in ms
   * @default 3000
   */
  reconnectInterval?: number;
  
  /**
   * Whether to show debug logs
   * @default process.env.NODE_ENV === 'development'
   */
  debug?: boolean;
  
  /**
   * The WebSocket URL to connect to
   * If not provided, will use the current server from the store
   */
  url?: string;
  
  /**
   * Optional authentication token to include in the connection URL
   */
  token?: string;
  
  /**
   * Optional user ID to include in the connection URL
   */
  userId?: string;
  
  /**
   * Whether to listen for server changes and reconnect
   * @default true
   */
  listenForServerChanges?: boolean;

  /**
   * Whether to enable heartbeat for latency measurement
   * @default true
   */
  enableHeartbeat?: boolean;

  /**
   * Heartbeat interval in milliseconds
   * @default 30000 (30 seconds)
   */
  heartbeatInterval?: number;

  /**
   * Number of missed heartbeats before considering connection dead
   * @default 3
   */
  missedHeartbeatsThreshold?: number;

  /**
   * Callback when a message is received
   */
  onMessage?: (message: any) => void;
  
  /**
   * Callback when the websocket connects
   */
  onConnect?: () => void;
  
  /**
   * Callback when the websocket disconnects
   */
  onDisconnect?: () => void;
  
  /**
   * Callback when a websocket error occurs
   */
  onError?: (error: Error) => void;
}

// Create an extended interface for our WebSocketMsg with id field
export interface WebSocketMsg extends StoreWebSocketMsg {
  id: string;
}

// Define SendMessageOptions
interface SendMessageOptions {
  queueIfDisconnected?: boolean;
  showToasts?: boolean;
  callback?: (success: boolean) => void;
}

// Helper function to get database name based on environment
function getDbName(): string {
  return getEnvironmentDatabaseName();
}

// Main hook implementation
export function useManagedWebSocket(options: UseManagedWebSocketOptions = {}): UseManagedWebSocketReturn {
  const logger = useLogger();
  
  // Get WebSocket store functions
  const { 
    setSocket,
    setConnectionStatus,
    setConnectionError,
    setLatency,
    setMissedPongs,
    addMessage: addMessageToStore,
    currentServer,
    socket: getSocket
  } = useWebSocketStore();
  
  // Set up default options
  const {
    autoConnect = true,
    reconnectAttempts = 10,
    reconnectInterval = 3000,
    debug = process.env.NODE_ENV === 'development',
    url,
    token,
    userId,
    listenForServerChanges = true,
    enableHeartbeat = true,
    heartbeatInterval = parseInt(process.env.NEXT_PUBLIC_WS_HEARTBEAT_INTERVAL || '30000', 10),
    missedHeartbeatsThreshold = 3,
    onMessage,
    onConnect,
    onDisconnect,
    onError
  } = options;

  // State
  const [readyState, setReadyState] = useState<ReadyState>(ReadyState.UNINSTANTIATED);
  const [connectionState, setConnectionState] = useState<string>('disconnected');
  const [latency, setLocalLatency] = useState<number | null>(null);
  const [socketInstance, setLocalSocketInstance] = useState<Socket | null>(null);
  const [missedPongs, setLocalMissedPongs] = useState<number>(0);

  // Refs
  const socketRef = useRef<Socket | null>(null);
  const missedPongsRef = useRef<number>(0);
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const serverRef = useRef<string | null>(currentServer);
  const latestToken = useRef<string | null>(token || null);
  const isConnecting = useRef<boolean>(false);
  const componentId = useRef<number>(++globalConnectionCount);
  // Track if this component has already attempted a connection
  const hasAttemptedConnection = useRef<boolean>(false);
  // Track pending messages to send after reconnection
  const pendingMessages = useRef<{type: string, data: any, options: SendMessageOptions}[]>([]);
  
  // Debug logging utility
  const log = useCallback((message: string, ...args: any[]) => {
    if (!debug) return;
    
    // Skip logging for heartbeat messages
    if (message.toLowerCase().includes('heartbeat') || 
        message.toLowerCase().includes('cannot send') ||
        message.toLowerCase().includes('already attempted connection') ||
        (args.length > 0 && 
         typeof args[0] === 'object' && 
         args[0]?.type === 'heartbeat')) {
      return;
    }
    
    // Skip logging for pong messages
    if (message.toLowerCase().includes('pong')) {
      return;
    }
    
    // Throttle repetitive logs
    if (message.startsWith('[WebSocket')) {
      const now = Date.now();
      const msgKey = `${message.substring(0, 30)}`;
      const lastLogTime = logTimestampsRef.current[msgKey] || 0;
      
      // Log only once every 5 seconds for repetitive messages
      if (now - lastLogTime < 5000) {
        return;
      }
      
      // Update the timestamp
      logTimestampsRef.current[msgKey] = now;
    }
    
    console.debug(`[WebSocket:${componentId.current}] ${message}`, ...args);
  }, [debug]);
  
  // Add a ref to track log timestamps for throttling
  const logTimestampsRef = useRef<Record<string, number>>({});

  // Build the WebSocket URL with authentication parameters
  const socketUrlMemo = useMemo(() => {
    // Use the provided URL or get from the store
    const baseUrl = url || (currentServer 
      ? WS_SERVERS[currentServer as ServerKey].url 
      : process.env.NODE_ENV === 'production' 
        ? WS_SERVERS.production.url 
        : WS_SERVERS.local.url); // Use production in production, local in development
    
    if (debug) {
      log('Determining connection URL:', { 
        providedUrl: url,
        storeServer: currentServer,
        selectedUrl: baseUrl,
        environment: process.env.NODE_ENV
      });
    }

    // Get the database name based on environment
    const dbName = getDbName();
    
    // Log the connection details for debugging
    log(`Preparing connection to ${baseUrl}`, { 
      server: currentServer,
      dbName,
      hasToken: !!token
    });
    
    return baseUrl;
  }, [url, currentServer, debug, token, log]);

  // =========== TOKEN VALIDATION ===========
  
  /**
   * Validates a JWT token format and expiration
   */
  const validateToken = useCallback((tokenToValidate: string | null): boolean => {
    if (!tokenToValidate) return false;
    
    try {
      // Check if token has three parts separated by dots
      const parts = tokenToValidate.split('.');
      if (parts.length !== 3) {
        console.warn('[WebSocket] Token format is invalid (not a valid JWT)');
        return false;
      }

      // Decode the payload to check expiration
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      if (payload.exp && payload.exp < currentTime) {
        console.warn(`[WebSocket] Token has expired at ${new Date(payload.exp * 1000).toLocaleString()}`);
        return false;
      }
      
      if (debug) {
        log(`Token is valid and expires at ${new Date(payload.exp * 1000).toLocaleString()}`);
      }
      
      return true;
    } catch (error) {
      console.error('[WebSocket] Failed to decode or validate token payload', error);
      return false;
    }
  }, [debug, log]);

  // Check server health to validate availability
  const checkServerAvailability = useCallback(async (): Promise<boolean> => {
    if (!socketUrlMemo) return false;
    
    try {
      const url = new URL(socketUrlMemo.replace('ws://', 'http://').replace('wss://', 'https://'));
      const healthEndpoint = `${url.origin}/health`;
      
      log(`Checking server health: ${healthEndpoint}`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      try {
        console.log('[WebSocket] Attempting health check to:', healthEndpoint);
        
        // First try a simple HEAD request which is less likely to be blocked by CORS
        const headResponse = await fetch(healthEndpoint, { 
          method: 'HEAD',
          cache: 'no-store',
          signal: controller.signal,
          mode: 'no-cors' // This allows the request to succeed even with CORS restrictions
        });
        
        clearTimeout(timeoutId);
        
        // If we get here with no-cors, we can't actually check the status code
        // but at least we know the server is responding
        console.log('[WebSocket] Health check HEAD request completed');
        log('Server health check passed (HEAD request)');
        return true;
      } catch (headError) {
        // If HEAD request fails, try a GET request
        console.warn('[WebSocket] Health check HEAD request failed, trying GET:', headError);
        
        try {
          const getResponse = await fetch(healthEndpoint, { 
            method: 'GET',
            headers: { 'Accept': 'application/json' },
            cache: 'no-store',
            signal: controller.signal,
            mode: 'cors',
            credentials: 'omit'
          });
          
          clearTimeout(timeoutId);
          
          console.log('[WebSocket] Health check GET response:', getResponse.status, getResponse.statusText);
          
          if (getResponse.ok) {
            const responseData = await getResponse.json();
            console.log('[WebSocket] Health check data:', responseData);
            log('Server health check passed (GET request)');
            return true;
          } else {
            console.warn(`[WebSocket] Server health check failed with status: ${getResponse.status}`);
            return false;
          }
        } catch (getError) {
          clearTimeout(timeoutId);
          console.warn(`[WebSocket] Server health check GET error:`, getError);
          
          // For development purposes, try to bypass health check failure during development
          if (process.env.NODE_ENV === 'development') {
            console.log('[WebSocket] Development mode: Bypassing health check failure for debugging');
            // Only bypass in development
            return true;
          }
          
          return false;
        }
      }
    } catch (error) {
      console.error('[WebSocket] Server health check error:', error);
      return false;
    }
  }, [socketUrlMemo, log]);

  // Get a fresh token
  const refreshToken = useCallback(async (): Promise<string | undefined> => {
    try {
      log('Fetching fresh token...');
      const freshToken = await getToken();
      
      if (!freshToken) {
        console.error('[WebSocket] Failed to get token');
        return undefined;
      }
      
      latestToken.current = freshToken;
      log('Successfully retrieved fresh token');
      
      return freshToken;
    } catch (error) {
      console.error('[WebSocket] Error getting fresh token:', error);
      return undefined;
    }
  }, [log]);

  // Clear all intervals and timers
  const clearAllTimers = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
      tokenRefreshIntervalRef.current = null;
    }
  }, []);

  // Disconnect if connected
  const disconnectIfConnected = useCallback(() => {
    if (socketRef.current) {
      try {
        log('Disconnecting from server...');
        socketRef.current.disconnect();
        
        // Clear state
        socketRef.current = null;
        setLocalSocketInstance(null);
        setSocket(null);
        setReadyState(ReadyState.CLOSED);
        setConnectionState('disconnected');
        setConnectionStatus('disconnected');
      } catch (error) {
        console.error('[WebSocket] Error during disconnect:', error);
      }
    }
    
    clearAllTimers();
  }, [clearAllTimers, setConnectionStatus, setSocket, log]);

  // Setup heartbeat for latency measurement
  const setupHeartbeat = useCallback((socket: Socket) => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    
    // Set up interval to send heartbeat pings
    heartbeatTimerRef.current = setInterval(() => {
      if (socket && socket.connected) {
        const startTime = Date.now();
        
        // Use standardized message format
        socket.emit('message', {
          type: 'heartbeat',
          data: { 
            action: 'ping',
            timestamp: startTime
          },
          timestamp: startTime
        });
        
        // Set a timeout to reset latency if no pong received
        const pongTimeoutId = setTimeout(() => {
          // If we don't get a pong back, increment missed pongs counter
          const newMissedPongs = missedPongsRef.current + 1;
          missedPongsRef.current = newMissedPongs;
          setLocalMissedPongs(newMissedPongs);
          setMissedPongs(newMissedPongs);
          
          setLocalLatency(null);
          setLatency(null);
        }, 5000); // 5 second timeout
        
        // Listen for pong response
        const pongHandler = (msg: WebSocketMsg) => {
          if (msg.type === 'heartbeat' && msg.data?.action === 'pong') {
            const endTime = Date.now();
            const newLatency = endTime - (msg.data?.timestamp || startTime);
            
            // Reset missed pongs when we get a response
            missedPongsRef.current = 0;
            setLocalMissedPongs(0);
            setMissedPongs(0);
            
            // Update latency state
            setLocalLatency(newLatency);
            setLatency(newLatency);
            
            // Clear timeout and remove listener
            clearTimeout(pongTimeoutId);
            socket.off('message', pongHandler);
          }
        };
        
        // Add temporary listener for this specific pong
        socket.on('message', pongHandler);
      } else {
        setLocalLatency(null);
        setLatency(null);
      }
    }, heartbeatInterval);
    
    // Also send an initial ping immediately
    if (socket && socket.connected) {
      const startTime = Date.now();
      
      // Use standardized message format for initial ping
      socket.emit('message', {
        type: 'heartbeat',
        data: { 
          action: 'ping',
          timestamp: startTime
        },
        timestamp: startTime
      });
      
      // Set a timeout for initial pong
      const initialPongTimeoutId = setTimeout(() => {
        setLocalLatency(null);
        setLatency(null);
      }, 5000);
      
      // Listen for initial pong response
      const initialPongHandler = (msg: WebSocketMsg) => {
        if (msg.type === 'heartbeat' && msg.data?.action === 'pong') {
          const endTime = Date.now();
          const newLatency = endTime - (msg.data?.timestamp || startTime);
          
          setLocalLatency(newLatency);
          setLatency(newLatency);
          
          // Clear timeout and remove listener
          clearTimeout(initialPongTimeoutId);
          socket.off('message', initialPongHandler);
        }
      };
      
      // Add temporary listener for initial pong
      socket.on('message', initialPongHandler);
    } else {
      setLocalLatency(null);
      setLatency(null);
    }
  }, [heartbeatInterval, setLatency, setMissedPongs]);

  // Setup token refresh interval
  const setupTokenRefreshInterval = useCallback(() => {
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
      tokenRefreshIntervalRef.current = null;
    }
    
    // Set up interval to refresh token periodically (30 minutes)
    tokenRefreshIntervalRef.current = setInterval(async () => {
      log('Refreshing token on schedule');
      const newToken = await refreshToken();
      if (newToken) {
        log('Token refreshed successfully on schedule');
        useWebSocketStore.getState().setAuthToken(newToken);
        
        // Update auth token in the socket if connected
        if (socketRef.current && socketRef.current.connected && socketRef.current.auth) {
          // Update socket auth token
          if (typeof socketRef.current.auth === 'object') {
            (socketRef.current.auth as any).token = newToken;
          }
        }
      }
    }, 30 * 60 * 1000); // Refresh every 30 minutes
  }, [log, refreshToken]);

  // Process pending messages after reconnection
  const processPendingMessages = useCallback(() => {
    if (pendingMessages.current.length === 0) return;
    
    if (socketRef.current && socketRef.current.connected) {
      log(`Processing ${pendingMessages.current.length} pending messages`);
      
      // Clone and clear the queue to avoid potential issues
      const messages = [...pendingMessages.current];
      pendingMessages.current = [];
      
      // Process each pending message
      messages.forEach(({ type, data, options }) => {
        // Directly emit the message instead of calling sendMessage to avoid circular reference
        if (socketRef.current && socketRef.current.connected) {
          try {
            socketRef.current.emit(type, data);
            if (options.callback) options.callback(true);
          } catch (err) {
            logger.error('Error sending queued WebSocket message:', err);
            if (options.callback) options.callback(false);
          }
        }
      });
    }
  }, [log, logger]);

  // Set up socket event handlers
  const setupSocketEventHandlers = useCallback((socket: Socket) => {
    // Clear existing listeners to avoid duplicates
    socket.off('connect');
    socket.off('disconnect');
    socket.off('error');
    socket.off('reconnect_attempt');
    socket.off('reconnect');
    socket.off('reconnect_error');
    socket.off('reconnect_failed');
    
    // Handle successful connection
    socket.on('connect', () => {
      log('Socket connected successfully');
      setReadyState(ReadyState.OPEN);
      setConnectionState('connected');
      setConnectionStatus('connected');
      setConnectionError(null);
      
      // Setup heartbeat for latency measurements
      if (enableHeartbeat) {
        setupHeartbeat(socket);
      }
      
      // Setup token refresh interval
      setupTokenRefreshInterval();
      
      // Process any pending messages
      processPendingMessages();
      
      // Execute onConnect callback if provided
      if (onConnect) onConnect();
      
      isConnecting.current = false;
      globalSocket = socket;
      globalConnectionAttemptInProgress = false;
    });
    
    // Handle disconnect event
    socket.on('disconnect', (reason: string) => {
      log(`Socket disconnected: ${reason}`);
      setReadyState(ReadyState.CLOSED);
      setConnectionState('disconnected');
      setConnectionStatus('disconnected');
      
      // Reset latency when disconnected
      setLocalLatency(null);
      setLatency(null);
      
      // Increment missed pongs on disconnection 
      missedPongsRef.current += 1;
      setLocalMissedPongs(missedPongsRef.current);
      setMissedPongs(missedPongsRef.current);
      
      if (onDisconnect) onDisconnect();
    });
    
    // Handle authentication errors
    socket.on('auth_error', (error: any) => {
      console.error('[WebSocket] Authentication error:', error);
      setConnectionError('Authentication error: ' + (error.message || 'Unknown error'));
      
      // Attempt to refresh token
      refreshToken().then(newToken => {
        if (newToken) {
          log('Token refreshed after auth error, reconnecting...');
          
          // Update the socket auth token for next reconnection
          if (socket.auth && typeof socket.auth === 'object') {
            (socket.auth as any).token = newToken;
          }
          
          // Reconnect with the new token
          socket.connect();
        }
      });
    });
    
    // Handle reconnection attempts
    socket.on('reconnect_attempt', (attemptNumber: number) => {
      log(`Reconnection attempt ${attemptNumber}`);
      setReadyState(ReadyState.CONNECTING);
      setConnectionState('connecting');
      setConnectionStatus('connecting');
      setConnectionError(`Reconnecting (attempt ${attemptNumber})...`);
    });
    
    // Handle successful reconnection
    socket.on('reconnect', (attemptNumber: number) => {
      log(`Successfully reconnected after ${attemptNumber} attempts`);
      setReadyState(ReadyState.OPEN);
      setConnectionState('connected');
      setConnectionStatus('connected');
      setConnectionError(null);
      
      // Process any pending messages
      processPendingMessages();
    });
    
    // Handle reconnection errors
    socket.on('reconnect_error', (error: Error) => {
      console.error('[WebSocket] Reconnection error:', error);
      setConnectionError('Reconnection error: ' + error.message);
    });
    
    // Handle reconnection failure
    socket.on('reconnect_failed', () => {
      log('Reconnection failed after maximum attempts');
      setReadyState(ReadyState.CLOSED);
      setConnectionState('disconnected');
      setConnectionStatus('disconnected');
      setConnectionError('Reconnection failed after maximum attempts. Please check your connection and try again.');
    });
    
    // Handle standard messages
    socket.on('message', (message: WebSocketMsg) => {
      log('Received message', message);
      addMessageToStore(message);
      
      // Execute callback if provided
      if (onMessage) onMessage(message);
    });
    
    // Handle all events for legacy compatibility
    socket.onAny((event: string, ...args: any[]) => {
      if (event !== 'message' && event !== 'pong' && !event.startsWith('_')) {
        // Create a standardized message format
        const message: WebSocketMsg = {
          id: crypto.randomUUID?.() || `msg-${Date.now()}`,
          type: event,
          data: args.length === 1 ? args[0] : args,
          timestamp: Date.now()
        };
        addMessageToStore(message);
        
        // Execute callback if provided
        if (onMessage) onMessage(message);
      }
    });

    // Handle connection errors
    socket.on('connect_error', (error) => {
      console.error('[WebSocket] Connection error:', error);
      
      // Reset latency when connection fails
      setLocalLatency(null);
      setLatency(null);
      
      // Handle auth errors specifically
      if (error.message && (
        error.message.includes('auth') || 
        error.message.includes('token') || 
        error.message.includes('authentication')
      )) {
        log('Authentication error detected, trying to refresh token');
        setConnectionError('Authentication failed: ' + error.message);
        
        // Try to refresh the token
        refreshToken().then(newToken => {
          if (newToken && socket) {
            // Update the socket auth token
            if (socket.auth && typeof socket.auth === 'object') {
              (socket.auth as any).token = newToken;
            }
          }
        });
      } else {
        setConnectionError('Connection error: ' + error.message);
      }
    });
  }, [
    addMessageToStore, 
    enableHeartbeat, 
    onConnect, 
    onDisconnect, 
    onMessage, 
    processPendingMessages, 
    refreshToken, 
    setConnectionError, 
    setConnectionStatus, 
    setLatency, 
    setMissedPongs, 
    setupHeartbeat, 
    setupTokenRefreshInterval
  ]);

  // Connect to the server using Socket.IO with proper reconnection
  const connect = useCallback(async () => {
    // Check if there's already a global connection
    if (globalSocket && globalSocket.connected) {
      log('Using existing global socket connection');
      socketRef.current = globalSocket;
      setLocalSocketInstance(globalSocket);
      setSocket(globalSocket);
      setReadyState(ReadyState.OPEN);
      setConnectionState('connected');
      setConnectionStatus('connected');
      setConnectionError(null);
      
      // Setup event handlers on the existing socket
      setupSocketEventHandlers(globalSocket);
      
      // Execute onConnect callback if provided
      if (onConnect) onConnect();
      
      return globalSocket;
    }
    
    // Prevent multiple concurrent connection attempts
    if (isConnecting.current || globalConnectionAttemptInProgress) {
      log('Connection already in progress, skipping');
      return null;
    }
    
    // Don't attempt connection without userId
    if (!userId) {
      log('Cannot connect without userId');
      setConnectionError('Missing userId');
      return null;
    }
    
    // Don't attempt connection without token
    if (!token && !latestToken.current) {
      log('Cannot connect without token');
      setConnectionError('Missing token');
      return null;
    }
    
    isConnecting.current = true;
    globalConnectionAttemptInProgress = true;
    
    setConnectionStatus('connecting');
    setConnectionState('connecting');
    setReadyState(ReadyState.CONNECTING);
    
    try {
      // Step 1: Check if server is available
      log('Checking server availability...');
      const isAvailable = await checkServerAvailability();
      if (!isAvailable) {
        console.warn('[WebSocket] Server health check failed');
        throw new Error('Server health check failed');
      }
      
      // Step 2: Validate token or get fresh one if needed
      let currentToken = latestToken.current || token;
      if (!currentToken || !validateToken(currentToken)) {
        log('Token invalid or missing, trying to get a fresh token');
        currentToken = await refreshToken();
        
        if (!currentToken) {
          setConnectionStatus('disconnected');
          setConnectionState('disconnected');
          setReadyState(ReadyState.CLOSED);
          setConnectionError('No valid token available');
          isConnecting.current = false;
          globalConnectionAttemptInProgress = false;
          return null;
        }
      }
      
      // Step 3: Establish connection
      log(`Connecting to Socket.IO server: ${socketUrlMemo}`);
      const dbName = getDbName();
      
      // In a real app, you'd do proper URL manipulation
      const socketUrl = new URL(socketUrlMemo.replace('ws://', 'http://').replace('wss://', 'https://'));
      
      // Always disconnect any existing connection first
      disconnectIfConnected();
      
      // Establish connection with token authentication
      log('Socket.IO configuration:', {
        url: socketUrl.origin,
        path: socketUrl.pathname !== '/' ? socketUrl.pathname : undefined,
        auth: { 
          token: currentToken,
          userId: userId || 'anonymous',
          dbName
        },
        reconnection: true,
        reconnectionAttempts: reconnectAttempts,
        reconnectionDelay: reconnectInterval,
        reconnectionDelayMax: reconnectInterval * 5,
        timeout: 10000
      });
      
      const socket = io(socketUrl.origin, {
        path: socketUrl.pathname !== '/' ? socketUrl.pathname : undefined,
        auth: { 
          token: currentToken,
          userId: userId || 'anonymous',
          dbName
        },
        // Enable built-in reconnection logic
        reconnection: true,
        reconnectionAttempts: reconnectAttempts,
        reconnectionDelay: reconnectInterval,
        reconnectionDelayMax: reconnectInterval * 5,
        randomizationFactor: 0.5,
        timeout: 10000,
        transports: ['websocket', 'polling'],
      });
      
      // Set up socket event listeners
      setupSocketEventHandlers(socket);
      
      // Set global socket instance
      globalSocket = socket;
      socketRef.current = socket;
      setLocalSocketInstance(socket);
      setSocket(socket);
      
      // Setup heartbeat immediately to measure latency once connected
      if (enableHeartbeat) {
        socket.on('connect', () => {
          setupHeartbeat(socket);
        });
      }
      
      // Connection is now in progress
      isConnecting.current = false;
      
      return socket;
    } catch (error) {
      console.error('[WebSocket] Connection failed:', error);
      setConnectionStatus('disconnected');
      setConnectionState('disconnected');
      setReadyState(ReadyState.CLOSED);
      setConnectionError(`Connection failed: ${error instanceof Error ? error.message : String(error)}`);
      isConnecting.current = false;
      globalConnectionAttemptInProgress = false;
      return null;
    }
  }, [
    checkServerAvailability,
    disconnectIfConnected,
    enableHeartbeat,
    onConnect,
    refreshToken,
    reconnectAttempts,
    reconnectInterval,
    setConnectionError,
    setConnectionStatus,
    setSocket,
    setupHeartbeat,
    setupSocketEventHandlers,
    socketUrlMemo,
    token,
    userId,
    validateToken,
  ]);

  // Effect to manage connection based on autoConnect flag
  useEffect(() => {
    // Don't do anything if autoConnect is disabled
    if (!autoConnect) {
      log('Not connecting - autoConnect disabled');
      return;
    }
    
    // Don't try to connect without userId
    if (!userId) {
      log('Not connecting - missing userId');
      return;
    }

    if (!token) {
      log('Not connecting - missing token');
      return;
    }
    
    // Store the latest token value
    latestToken.current = token;
    
    // Check if we've already attempted a connection in this component instance
    if (hasAttemptedConnection.current) {
      log('Already attempted connection in this component instance');
      return;
    }
    
    // Mark that we've attempted a connection
    hasAttemptedConnection.current = true;
    
    log('Auto-connecting to WebSocket');
    
    // Create connection
    connect()
      .catch(error => {
        console.error('[WebSocket] Failed to connect:', error);
        setConnectionError(error instanceof Error ? error.message : String(error));
      });
    
    return () => {
      // Clean up when component unmounts
      disconnectIfConnected();
    };
  }, [autoConnect, token, userId, connect, disconnectIfConnected, log, setConnectionError]);

  // Listen for server changes if enabled
  useEffect(() => {
    if (!listenForServerChanges) return;
    
    // Don't reconnect if the server hasn't changed
    if (serverRef.current === currentServer) return;
    
    log('Server changed, reconnecting...');
    serverRef.current = currentServer;
    
    // Don't reconnect if we have no server information
    if (!currentServer) return;
    
    // Disconnect and reconnect with a small delay to allow state to settle
    setTimeout(() => {
      if (isConnecting.current) return;
      disconnectIfConnected();
      connect();
    }, 500);
  }, [currentServer, connect, disconnectIfConnected, listenForServerChanges, log]);

  // Function to send a message
  const sendMessage = useCallback((type: string, data: any, options: SendMessageOptions = {}) => {
    const { queueIfDisconnected = true, showToasts = true, callback } = options;
    
    const message: WebSocketMsg = {
      id: crypto.randomUUID?.() || `msg-${Date.now()}`,
      type,
      data,
      timestamp: Date.now()
    };
    
    // Store the message in our store for debugging
    addMessageToStore(message);
    
    if (socketRef.current && socketRef.current.connected) {
      log('Sending message:', { type, data });
      
      try {
        socketRef.current.emit(type, data);
        if (callback) callback(true);
        return true;
      } catch (err) {
        logger.error('Error sending WebSocket message:', err);
        if (callback) callback(false);
        return false;
      }
    } else {
      log('Cannot send message, socket not connected', { type, data });
      
      // Queue message if requested
      if (queueIfDisconnected) {
        log('Message queued for later delivery', { type, data });
        pendingMessages.current.push({ type, data, options });
        if (callback) callback(false);
      } else if (callback) {
        callback(false);
      }
      
      return false;
    }
  }, [addMessageToStore, log, logger]);

  return {
    sendMessage,
    readyState,
    connectionState: connectionState,
    socketUrl: socketUrlMemo,
    latency,
    socket: socketRef.current,
    checkServerAvailability
  };
}

export default useManagedWebSocket; 